import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { ApiError } from '../api/client';
import { useHrMutation, useLeaveTypes, useMyLeave, useMyPayslips } from '../api/hr';
import { formatDate, formatGhs, type Tone } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, Modal, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';
import { ClockCard } from './ClockCard';

const monthName = (p: string) => new Date(`${p}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const TONE: Record<string, Tone> = { PENDING: 'warn', APPROVED: 'good', REJECTED: 'bad', CANCELLED: '' };
const LABEL: Record<string, string> = { PENDING: 'Waiting', APPROVED: 'Approved', REJECTED: 'Refused', CANCELLED: 'Cancelled' };

/** Every member of staff: their own payslips and leave. */
export function MyPay() {
  const slips = useMyPayslips();
  const leave = useMyLeave();
  const toast = useToast();
  const report = useReport();
  const [asking, setAsking] = useState(false);
  const cancel = useHrMutation((id: string) => api(`/me/leave/${id}/cancel`, { method: 'POST', body: {} }));

  const notLinked = (slips.error instanceof ApiError && slips.error.status === 404) || (leave.error instanceof ApiError && leave.error.status === 404);
  if (notLinked) {
    return (
      <div className="page">
        <PageHead eyebrow="Me" title="My pay and leave" />
        <div className="card flat">
          <Empty icon="people" title="Your login is not linked to a staff record yet">Ask the manager or accountant to link them, and your payslips and leave will appear here.</Empty>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHead eyebrow="Me" title="My pay and leave">
        <Button variant="primary" onClick={() => setAsking(true)}>
          Ask for leave
        </Button>
      </PageHead>

      <ClockCard />

      <section className="stack">
        <div className="eyebrow">Payslips</div>
        {slips.isLoading ? (
          <Loading rows={2} />
        ) : slips.error ? (
          <ErrorNote error={slips.error} retry={() => void slips.refetch()} />
        ) : (slips.data ?? []).length === 0 ? (
          <div className="card flat"><Empty icon="inbox" title="No payslips yet">A payslip appears here once the month’s payroll is approved.</Empty></div>
        ) : (
          <div className="list">
            {slips.data!.map((p) => (
              <Link key={p.runId} to={`/my-pay/${p.runId}`} className="item">
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="title">{monthName(p.period)}</span>
                  <Badge tone={p.status === 'PAID' ? 'good' : 'info'}>{p.status === 'PAID' ? 'Paid' : 'Approved'}</Badge>
                </div>
                <span className="small muted">Take-home <strong className="num">{formatGhs(p.net)}</strong></span>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="stack">
        <div className="eyebrow">Leave in {leave.data?.year ?? ''}</div>
        {leave.isLoading ? (
          <Loading rows={2} />
        ) : leave.data ? (
          <>
            <ul className="checklist">
              {leave.data.balances.map((b) => (
                <li key={b.leaveType}>
                  <div className="row" style={{ justifyContent: 'space-between', minHeight: 40 }}>
                    <span>{b.leaveType}</span>
                    <span className="small muted num">{b.remaining === null ? `${b.taken} taken` : `${b.remaining} left of ${b.entitlement}`}</span>
                  </div>
                </li>
              ))}
            </ul>
            <div className="list">
              {leave.data.requests.map((r) => (
                <div key={r.id} className="item">
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <span className="title">{r.leaveType}</span>
                    <Badge tone={TONE[r.status] ?? ''}>{LABEL[r.status]}</Badge>
                  </div>
                  <span className="small muted">{formatDate(r.from)} to {formatDate(r.to)} · {r.days} working day{r.days === 1 ? '' : 's'}</span>
                  {r.status === 'PENDING' ? (
                    <div>
                      <Button variant="ghost" loading={cancel.isPending} onClick={() => cancel.mutate(r.id, { onError: report, onSuccess: () => toast.ok('Cancelled') })}>
                        Cancel request
                      </Button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </>
        ) : null}
      </section>
      {asking ? <AskDialog onClose={() => setAsking(false)} /> : null}
    </div>
  );
}

function AskDialog({ onClose }: { onClose: () => void }) {
  const types = useLeaveTypes();
  const toast = useToast();
  const report = useReport();
  const [typeId, setTypeId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const make = useHrMutation((body: Record<string, unknown>) => api('/me/leave', { method: 'POST', body }));
  useEffect(() => { if (!typeId && types.data?.[0]) setTypeId(types.data[0].id); }, [types.data, typeId]);
  const valid = !!typeId && !!from && !!to && from <= to;
  return (
    <Modal open onClose={onClose} title="Ask for leave">
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (valid) make.mutate({ leaveTypeId: typeId, from, to, ...(reason.trim() ? { reason: reason.trim() } : {}) }, { onError: report, onSuccess: () => { toast.ok('Sent for approval'); onClose(); } });
        }}
      >
        <Field id="ak-type" label="Kind of leave">
          <select id="ak-type" className="select" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
            {(types.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}{t.paid ? '' : ' (unpaid)'}</option>)}
          </select>
        </Field>
        <div className="grid-2">
          <Field id="ak-from" label="First day"><input id="ak-from" className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
          <Field id="ak-to" label="Last day" hint="Weekends are not counted"><input id="ak-to" className="input" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} /></Field>
        </div>
        <Field id="ak-why" label="Reason (optional)"><input id="ak-why" className="input" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={make.isPending}>Send request</Button>
        </div>
      </form>
    </Modal>
  );
}
